import React, { useState } from 'react';
import { SaveButton as RASaveButton, Toolbar, /*TextInput,*/ useRefresh, useNotify, useRecordContext, useTranslate } from 'react-admin';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SendIcon from '@mui/icons-material/Send';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useWatch, useFormContext } from 'react-hook-form';

import SimpleForm from '../../common/forms/SimpleForm';
import IconButton from '../../common/buttons/IconButton';

import { sendInvoice } from '../../../services/api';

const toolbarSx = {
	padding: '0 !important',
	minHeight: 'auto !important',
	backgroundColor: 'transparent',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: '15px'
};

const SaveButton = ({ onClick, ...props }) => {
	const record = useRecordContext();
	const { setValue } = useFormContext();
	const subject = useWatch({ name: 'subject' });
	const message = useWatch({ name: 'message' });
	const notify = useNotify();

	const handleClick = async () => {
		let data = {};
		if (subject) {
			data.subject = subject;
		}
		if (message) {
			data.message = message;
		}

		const response = await sendInvoice(record.id, JSON.stringify(data));
		if (response.success || response.id) {
			notify('Invoice sent');
		}
		else if (!response.success && response.message) {
			notify(response.message, 'warning');
		}
		else {
			notify('Sending the invoice failed', 'warning');
		}

		setValue('subject', '');
		setValue('message', '');
		onClick();
	};

	return <RASaveButton {...props} onClick={handleClick} label="Send" icon={<SendIcon />} alwaysEnable />;
};

const CustomToolbar = ({ onSave, onClose }) => {
	const translate = useTranslate();

	return (
		<Toolbar sx={toolbarSx}>
			<IconButton icon={<ErrorOutlineIcon />} onClick={onClose}>{translate('ra.action.cancel')}</IconButton>
			<SaveButton onClick={onSave} />
		</Toolbar>
	);
};

const SendInvoiceButton = () => {
	const record = useRecordContext();
	const refresh = useRefresh();
	const translate = useTranslate();
	const notify = useNotify();
	const [showDialog, setShowDialog] = useState(false);
	const [showWarning, setShowWarning] = useState(false);

	const handleSave = async () => {
		handleClose();
		refresh();
	};

	const handleOpen = () => {
		// Check if invoicing address is on PEPPOL or if PEPPOL validation is missing
		const addressNotOnPeppol = record?.invoicing_address && record.invoicing_address.is_present_on_peppol === false;
		const peppolNotValidated = record?.peppol_validated === false || record?.peppol_validated === null;

		if (addressNotOnPeppol || peppolNotValidated) {
			setShowWarning(true);
		} else {
			setShowDialog(true);
		}
	};

	const handleClose = () => {
		setShowDialog(false);
		setShowWarning(false);
	};

	const handleContinueAnyway = () => {
		setShowWarning(false);
		setShowDialog(true);
	};

	if (!record) {
		return null;
	}

	return (
		<>
			<IconButton icon={<SendIcon />} onClick={handleOpen}>Send</IconButton>

			{/* Warning dialog for PEPPOL issues */}
			<Dialog fullWidth open={showWarning} onClose={handleClose} aria-label="PEPPOL warning">
				<DialogTitle>PEPPOL Issues</DialogTitle>
				<DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
					{record?.invoicing_address && record.invoicing_address.is_present_on_peppol === false && (
						<Alert severity="warning" icon={<WarningIcon />}>
							The invoicing address for this invoice is <strong>not registered on the PEPPOL network</strong>.
							The invoice will be sent by email only, not via PEPPOL.
						</Alert>
					)}
					{(record?.peppol_validated === false || record?.peppol_validated === null) && (
						<Alert severity="warning" icon={<WarningIcon />}>
							The invoice <strong>has not been validated against PEPPOL standards</strong>.
							Please validate the invoice before sending to ensure compliance.
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">
						Cancel
					</Button>
					<Button onClick={handleContinueAnyway} color="primary" variant="contained">
						Send Anyway
					</Button>
				</DialogActions>
			</Dialog>

			{/* Send invoice dialog */}
			<Dialog fullWidth open={showDialog} onClose={handleClose} aria-label="Send invoice">
				<DialogTitle>{translate('Send invoice')}</DialogTitle>
				<DialogContent>
					{translate('Do you want to send the invoice %{number}?', { number: record.number })}
					<SimpleForm toolbar={<CustomToolbar onSave={handleSave} onClose={handleClose} />} record={record}>
						{/* TODO: uncomment for future step */}
						{/* <TextInput source="subject" helperText={false} fullWidth />
						<TextInput source="message" options={{ multiline: true }} rows="4" helperText={false} fullWidth /> */}
					</SimpleForm>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SendInvoiceButton;